import React, { useState } from 'react';
import { NextUI } from '@/components/NextUI';
import { useAppDispatch } from '@/hooks/redux';
import { showModal } from '@/store/slices/modalSlice';
import { emit } from '@/utils/emitter';
import Link from 'next/link';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import { useParams } from 'next/navigation';
import {
  getDownloadSignupErrorMessage,
  isValidDownloadEmail,
  useDownloadEmailSignup,
} from '@/features/tool/hooks/useDownloadEmailSignup';

const DownloadSignupModal = ({
  data,
  hideModal,
}: {
  data?: any;
  hideModal: () => void;
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { submitEmail } = useDownloadEmailSignup();
  const params = useParams();
  const localeParam = Array.isArray(params?.locale)
    ? params.locale[0]
    : params?.locale;
  const invalidEmailMessage =
    localeParam === 'tr'
      ? 'Lütfen geçerli bir e-posta adresi girin.'
      : 'Please enter a valid email address.';
  const isValidEmail = isValidDownloadEmail;
  const handleClose = React.useCallback(() => {
    data?.onCancel?.();
    hideModal();
  }, [data, hideModal]);

  React.useEffect(() => {
    const source = buildStructuredAnalyticsSource(
      data?.toolType,
      data?.originEntryPoint
    );

    logHubxEvent({
      eventName: APP_EVENTS.LND_EMAIL,
      source: source.toolType,
      event_params: {
        source,
      },
    });
  }, [data?.originEntryPoint, data?.toolType]);

  const handleSignup = async () => {
    if (!email) return;

    if (!isValidEmail(email)) {
      setValidationError(invalidEmailMessage);
      return;
    }

    const source = buildStructuredAnalyticsSource(
      data?.toolType,
      data?.originEntryPoint
    );

    logHubxEvent({
      eventName: APP_EVENTS.BTN_DOWNLOAD_EMAIL,
      source: source.toolType,
      event_params: {
        source,
      },
    });
    setIsLoading(true);
    setError(null);
    setValidationError(null);

    const normalizedEmail = email.trim().toLowerCase();
    setEmail(normalizedEmail);

    try {
      const result = await submitEmail(normalizedEmail, data);

      if (result === 'verification') {
        return;
      }

      hideModal();

      if (data?.onSuccess) {
        await data.onSuccess();
      } else {
        emit('download:step-completed', {
          extension: data?.extension,
          toolType: data?.toolType,
          originEntryPoint: data?.originEntryPoint,
        });
      }
    } catch (signupError) {
      setError(getDownloadSignupErrorMessage(signupError));
    } finally {
      setIsLoading(false);
    }
  };

  const openLogin = () => {
    dispatch(showModal({ modalKey: 'DownloadLogin', modalData: data }));
  };

  return (
    <NextUI.Modal
      className="max-w-[500px] max-h-[90vh] rounded-none"
      classNames={{ content: 'py-8 px-8 !overflow-y-auto' }}
      defaultOpen={true}
      name={'DownloadSignup'}
      backdrop="blur"
      scrollBehavior="inside"
      hideCloseButton={false}
      onClose={handleClose}
    >
      <div className="flex flex-col items-center">
        <h2 className="text-[28px] font-bold text-dark text-start leading-tight mb-[10px]">
          To download your file, create an account
        </h2>
        <p className="text-gray-medium text-start mb-4">
          Save your changes, download instantly, and access all features.
        </p>

        <div className="w-full space-y-5">
          <NextUI.Input
            type="text"
            inputMode="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              const nextValue = e.target.value;
              setEmail(nextValue);
              if (validationError && (!nextValue || isValidEmail(nextValue))) {
                setValidationError(null);
              }
            }}
            onBlur={() => {
              if (!email) return;
              if (!isValidEmail(email)) {
                setValidationError(invalidEmailMessage);
              }
            }}
            validationBehavior="aria"
            isInvalid={Boolean(validationError)}
            errorMessage={validationError}
            variant="outline"
            radius="sm"
            className="w-full"
            classNames={{
              input: 'text-base md:text-sm placeholder:text-gray-medium',
              inputWrapper:
                'bg-white border border-gray-200 h-12 shadow-none hover:border-blue-500 focus-within:border-blue-500 transition-colors',
            }}
          />

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <NextUI.Button
            className="w-full h-12 text-white font-semibold text-lg transition-colors"
            onClick={handleSignup}
            isLoading={isLoading}
          >
            Download File
          </NextUI.Button>
        </div>

        <p className="text-[13px] text-gray-medium mt-3 text-start leading-relaxed">
          By clicking <span className="text-medium">“Download File”</span> you
          agree to our{' '}
          <Link
            target="_blank"
            href="/terms"
            className="text-primary-blue hover:underline text-medium"
          >
            Terms and Conditions
          </Link>{' '}
          <span className="text-primary-blue text-medium">and</span>{' '}
          <Link
            target="_blank"
            href="/privacy"
            className="text-primary-blue hover:underline text-medium"
          >
            Privacy Policy
          </Link>
        </p>

        <div className="mt-4 border-t border-gray-100 w-full pt-4 text-start">
          <p className="text-gray-medium text-sm">
            Used pdfgenius.com before?{' '}
            <button
              onClick={openLogin}
              className="text-primary-blue font-semibold hover:underline"
            >
              Log In
            </button>
          </p>
        </div>
      </div>
    </NextUI.Modal>
  );
};

export default DownloadSignupModal;
